import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSubmitted(true);
      toast.success('Check your email for reset instructions!');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-edupaws-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-edupaws-sage" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-gray-800 mb-4">
            Check Your Email
          </h1>
          <p className="text-gray-500 font-body mb-8">
            If an account exists for <strong>{email}</strong>, we've sent password reset instructions.
          </p>
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="inline-block font-heading font-bold py-4 px-10 rounded-full text-lg text-white transition-all
              bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px]"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <button 
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-8 font-body"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </button>

        <div className="flex items-center gap-3 mb-8">
          <img 
            src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=100" 
            alt="Edu Paws" 
            className="h-10"
          />
        </div>

        <div className="w-16 h-16 bg-edupaws-sage/20 rounded-2xl flex items-center justify-center mb-6">
          <Mail className="w-8 h-8 text-edupaws-sage" />
        </div>

        <h1 className="font-heading text-3xl font-bold text-gray-800 mb-2">
          Forgot Password?
        </h1>
        <p className="text-gray-500 font-body mb-8">
          No worries! Enter your email and we'll send you reset instructions.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="font-body font-medium text-gray-700">
              Email Address
            </Label>
            <Input
              id="email"
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="h-12 rounded-xl border-2 border-gray-200 focus:border-edupaws-navy font-body"
            />
          </div>

          <button
            type="submit"
            data-testid="reset-submit"
            disabled={loading}
            className="w-full font-heading font-bold py-4 rounded-full text-lg text-white transition-all
              bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px]
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <p className="mt-6 text-center text-gray-500 font-body">
          Remember your password?{' '}
          <Link to="/login" className="text-edupaws-sage hover:text-edupaws-navy font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
